import { Injectable } from '@angular/core';
import { MotesLoginService } from '@motes-sdk/motes-login';
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { environment } from '@env/environment';
import { firstValueFrom, map, Observable } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { createViewQuery, QueryBuilderView } from './realtime-query-view';
import { Database } from '../models/supabase-types';
import { JobProgress, JobProgressSum, Story, Tour } from '../models/db-types';
import { Mote, MoteType } from '../models';

@Injectable({ providedIn: 'root' })
export class SupabaseService {
    // Cached supabase client
    private supabaseClient: SupabaseClient<Database>;
    private anonSupabaseClient: SupabaseClient<Database>;
    private cachedToken: string;

    constructor(private httpClient: HttpClient, private loginService: MotesLoginService) {
    }

    public async getSupabaseClient(enableTokenRefresh: boolean = true) : Promise<SupabaseClient<Database>>  {
        const firebaseToken = await this.loginService.getIdToken();
        // Check if the firebase token is still the same and in that case return the cached supabase client
        if(this.supabaseClient && this.cachedToken == firebaseToken) {
            return this.supabaseClient;
        }
        // Get supabase token
        const supabaseToken = await this.mintSupabaseToken(firebaseToken);
        // Make sure cached client is returned in case this function was invoked again while waiting for token and this.supabaseClient was not set yet
        if(this.supabaseClient && this.cachedToken == firebaseToken) {
            return this.supabaseClient;
        }

        // Cache fb token
        this.cachedToken = firebaseToken;
        // Create new supabase client
        this.supabaseClient = this.createSupabaseClient(`Bearer ${supabaseToken}`, enableTokenRefresh);
        return this.supabaseClient;
    }

    /**
     * Create a new anon supabase client if no authorized client exist
     */
    public getAnonSupabaseClient() : SupabaseClient<Database> {
        // Returned cached client
        if(this.supabaseClient || this.anonSupabaseClient) return this.supabaseClient || this.anonSupabaseClient;
        // Create a new anon supabase client if no authorized client exist
        this.anonSupabaseClient = this.createSupabaseClient();
        return this.anonSupabaseClient;
    }

    public createSupabaseClient(authorizationHeader: string = environment.SUPABASE_ANON_KEY, enableTokenRefresh: boolean = false): SupabaseClient<Database> {
        // console.log("Create client connection to:", Deno.env.get('SUPABASE_URL'));
        if(!authorizationHeader?.startsWith("Bearer")) console.warn("The authorization header should probably start with 'Bearer', trying to continue anyway..");

        const supabaseClient = createClient<Database>(
            environment.SUPABASE_URL,
            environment.SUPABASE_ANON_KEY,
            {
                global: {
                    headers: { Authorization: authorizationHeader }
                },
                auth: {
                    autoRefreshToken: enableTokenRefresh,
                    persistSession: enableTokenRefresh,
                }
            }
        );
        // This is needed in order for realtime to use custom token (TODO: it also needs to be refreshed?)
        supabaseClient.realtime.setAuth(authorizationHeader.replace('Bearer ', ''));
        return supabaseClient;
    }

    public async mintSupabaseToken(firebaseToken: string) : Promise<string>{
        const MINT_ENDPOINT = `${environment.STORYHUNT_LRP_API}/auth/mint-token`;

        const rsp = await firstValueFrom(this.httpClient.post<{claims: any, token: string}>(MINT_ENDPOINT, { firebaseToken }, { observe: 'response' })).catch(err => {
            console.log("Could not mint supabase token: ", err);
            throw err as HttpErrorResponse;
        });
        return rsp.body.token;
    }

    public watchJobProgress(db: SupabaseClient<Database>, parentProgressId: string) : Observable<JobProgressSum | null>{
        // Create query function for progress sum
        const itemViewQueryFn: () => QueryBuilderView<'supaqueue', 'job_progress_sum'> = () => db.schema('supaqueue').from('job_progress_sum').select('*');
        // Use the parent job id if it is defined otherwise use the id of the job to find the job sum
        const getForeignViewItemId = (item: JobProgress) => {
            return item['parent_job_id'] || item['id'];
        }

        // Create view query
        const query = createViewQuery(db, 'supaqueue', 'job_progress', 'job_progress_sum', 'parent_job_id', 'job_id', itemViewQueryFn, getForeignViewItemId);

        // Watch progress sum updates
        return query.watchDbItemsQuery(itemViewQueryFn().eq('job_id', parentProgressId), `parent_job_id=eq.${parentProgressId}`).pipe(map((progressList) => {
            return progressList.length > 0 ? progressList[0] : null;
        }));
    }

    public convertTourToSupabase(tour: Mote) : Tour {
        const tourJson = tour.toJson();

        // Map firebase tour to supabase
        const supabaseTour:Tour = {
            key: tour.key,
            deletedOn: (tour.deletedOn ? new Date(tour.deletedOn) : null)?.toISOString() || null,
            createdOn: (tour.createdOn ? new Date(tour.createdOn) : null)?.toISOString() || null,
            updatedOn: (tour.updatedOn ? new Date(tour.updatedOn) : null)?.toISOString() || null,
            publishedOn: (tour.publishedOn ? new Date(tour.publishedOn) : null)?.toISOString() || null,
            type: this.moteTypeToString(tour.type),
            quiz: tourJson.content?.quiz || null,
            media: tourJson.content?.media || null,
            sneakPeakMedia: tourJson.content?.sneakPeakMedia || null,
            sneakPeekHeader: tour.sneakPeekHeader,
            sneakPeekText: tour.sneakPeekText,
            address: tour.address,
            city: tour.city,
            country: tour.country,
            createdBy: tour.createdBy,
            duration: tour.duration,
            endPlace: tour.endPlace,
            externalLink: tour.externalLink,
            externalLinkText: tour.externalLinkText,
            hideTitle: tour.hideTitle,
            isCustomTitle: tour.isCustomTitle,
            keyOptimized: tour.keyOptimized,
            language: tour.language,
            latitude: tour.position?.lat,
            longitude: tour.position?.long,
            length: tour.length,
            motesLink: tour.motesLink,
            motesLinkText: tour.motesLinkText,
            order: tour.order,
            originKey: tour.originKey,
            parent: tour.parent,
            previewKey: tour.previewKey,
            previewText: tour.previewText,
            price: tour.price,
            productLocked: tour.productLocked,
            products: tour.products,
            project: tour.project,
            routeGeometry: tour.routeGeometry,
            sortBy: tour.sortBy,
            startPlace: tour.startPlace,
            storyCount: tour.storyCount,
            tags: tour.tags,
            text: tour.text,
            title: tour.title,
            worldCityId: tour.worldCityId,
            // Deprecated
            published: null,
        }

        return supabaseTour;
    }

    public convertStoryToSupabase(story: Mote) : Story {
        const tourJson = story.toJson();

        // Map firebase tour to supabase
        const supabaseStory:Story = {
            key: story.key,
            deletedOn: (story.deletedOn ? new Date(story.deletedOn) : null)?.toISOString() || null,
            createdOn: (story.createdOn ? new Date(story.createdOn) : null)?.toISOString() || null,
            updatedOn: (story.updatedOn ? new Date(story.updatedOn) : null)?.toISOString() || null,
            publishedOn: (story.publishedOn ? new Date(story.publishedOn) : null)?.toISOString() || null,
            type: this.moteTypeToString(story.type),
            quiz: tourJson.content?.quiz || null,
            media: tourJson.content?.media || null,
            sneakPeakMedia: tourJson.content?.sneakPeakMedia || null,
            sneakPeekHeader: story.sneakPeekHeader,
            sneakPeekText: story.sneakPeekText,
            address: story.address,
            city: story.city,
            country: story.country,
            createdBy: story.createdBy,
            duration: story.duration,
            endPlace: story.endPlace,
            externalLink: story.externalLink,
            externalLinkText: story.externalLinkText,
            hideTitle: story.hideTitle,
            isCustomTitle: story.isCustomTitle,
            keyOptimized: story.keyOptimized,
            language: story.language,
            latitude: story.position?.lat,
            longitude: story.position?.long,
            length: story.length,
            motesLink: story.motesLink,
            motesLinkText: story.motesLinkText,
            order: story.order,
            originKey: story.originKey,
            parent: story.parent,
            previewKey: story.previewKey,
            previewText: story.previewText,
            price: story.price,
            productLocked: story.productLocked,
            products: story.products,
            project: story.project,
            routeGeometry: story.routeGeometry,
            sortBy: story.sortBy,
            startPlace: story.startPlace,
            storyCount: story.storyCount,
            tags: story.tags,
            text: story.text,
            title: story.title,
            // Deprecated
            published: null,
        }

        return supabaseStory;
    }

    private moteTypeToString(type: MoteType): string {
        const typeNumber = parseInt(type + "");
        switch(typeNumber) {
            case MoteType.COLLECTION: return "COLLECTION";
            case MoteType.ROUTE: return "ROUTE";
            case MoteType.PLACE: return "PLACE";
            case MoteType.EVENT: return "EVENT";
            case MoteType.QUEST: return "QUEST";
            case MoteType.SCHEDULE: return "SCHEDULE";
            default: console.warn("Could not convert type '" + type + "' ...?");
            return "ROUTE";
        }
    }
}
